import React from "react";
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCachedFacebookPages } from "@/hooks/useCachedFacebookPages";
import { useCachedInstagramAccounts } from "@/hooks/useCachedInstagramAccounts";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

interface SetupAdProfilesProps {
  adAccountId: string;
  data: {
    default_fb_page_id: string | null;
    default_fb_page_name: string | null;
    default_ig_account_id: string | null;
    default_ig_account_name: string | null;
  };
  onChange: (data: Partial<SetupAdProfilesProps["data"]>) => void;
}

export function SetupAdProfiles({ adAccountId, data, onChange }: SetupAdProfilesProps) {
  const { user } = useAuth();
  const { data: pages, isLoading: pagesLoading } = useCachedFacebookPages(user?.id);
  const { data: igAccounts, isLoading: igLoading } = useCachedInstagramAccounts(user?.id);

  // Syncing state: show spinner for up to 15s if data is empty
  const [isWaitingForSync, setIsWaitingForSync] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setIsWaitingForSync(false), 30000);
    return () => clearTimeout(timer);
  }, []);

  const filteredPages = (pages ?? []).filter(
    (p) => !p.ad_account_id || p.ad_account_id === adAccountId
  );
  const filteredIg = (igAccounts ?? []).filter(
    (a) => !a.ad_account_id || a.ad_account_id === adAccountId
  );

  const pagesEmpty = filteredPages.length === 0;
  const igEmpty = filteredIg.length === 0;
  const showPagesLoading = pagesLoading || (isWaitingForSync && pagesEmpty);
  const showIgLoading = igLoading || (isWaitingForSync && igEmpty);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Ad Profiles</CardTitle>
        <CardDescription>Default Facebook Page and Instagram Account for new ads</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Facebook Page */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Default Facebook Page</label>
          <Select
            value={data.default_fb_page_id ?? ""}
            onValueChange={(val) => {
              const page = filteredPages.find((p) => p.page_id === val);
              onChange({
                default_fb_page_id: val,
                default_fb_page_name: page?.page_name ?? null,
              });
            }}
            disabled={showPagesLoading}
          >
            <SelectTrigger>
              {showPagesLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading pages...</span>
                </div>
              ) : (
                <SelectValue placeholder="Select a Facebook Page..." />
              )}
            </SelectTrigger>
            <SelectContent>
              {pagesEmpty ? (
                <SelectItem value="__none" disabled>
                  No pages found yet. In some situations syncing can take a bit longer — please wait a moment or try refreshing the page.
                </SelectItem>
              ) : (
                filteredPages.map((p) => (
                  <SelectItem key={p.page_id} value={p.page_id}>
                    {p.display_name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Instagram Account */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Default Instagram Account</label>
          <Select
            value={data.default_ig_account_id ?? ""}
            onValueChange={(val) => {
              const acct = filteredIg.find((a) => a.instagram_id === val);
              onChange({
                default_ig_account_id: val,
                default_ig_account_name: acct?.username ?? null,
              });
            }}
            disabled={showIgLoading}
          >
            <SelectTrigger>
              {showIgLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading accounts...</span>
                </div>
              ) : (
                <SelectValue placeholder="Select an Instagram Account..." />
              )}
            </SelectTrigger>
            <SelectContent>
              {igEmpty ? (
                <SelectItem value="__none" disabled>
                  No accounts found yet. In some situations syncing can take a bit longer — please wait a moment or try refreshing the page.
                </SelectItem>
              ) : (
                filteredIg.map((a) => (
                  <SelectItem key={a.instagram_id} value={a.instagram_id}>
                    {a.display_name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}
